/**
 * StorageStatusCard — honest local + cloud storage overview, shared by
 * MorePage and DataPage.
 *
 * - Local drafts: counts entries under the known device-only draft keys
 *   ('rust-journal-drafts', 'rust-places-drafts'). These are drafts, never
 *   cloud-saved data.
 * - Cloud: reflects isCloudConfigured() — 'Nee' means repositories cannot
 *   reach Supabase and data stays on this device.
 * - Pending writes: live queue size via useSync().
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card } from '@/components/ui'
import { isCloudConfigured } from '@/lib/supabase/client'
import { useSync } from '@/services/sync'
import { PATHS } from '@/app/router/paths'
import { JOURNAL_DRAFTS_STORAGE_KEY } from '@/features/journal/journalDrafts'
import { PLACES_DRAFTS_STORAGE_KEY } from '@/features/places/drafts'

function countDraftEntries(key: string): number {
  if (typeof localStorage === 'undefined') return 0
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return 0
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return 0
    return Object.keys(parsed as Record<string, unknown>).length
  } catch {
    return 0
  }
}

export function countLocalDrafts(): number {
  return countDraftEntries(JOURNAL_DRAFTS_STORAGE_KEY) + countDraftEntries(PLACES_DRAFTS_STORAGE_KEY)
}

export function StorageStatusCard() {
  const { pendingWrites, online } = useSync()
  const [draftCount, setDraftCount] = useState<number>(() => countLocalDrafts())
  const cloud = isCloudConfigured()

  // Refresh the draft count when this tab writes drafts or another tab does.
  useEffect(() => {
    const refresh = () => setDraftCount(countLocalDrafts())
    window.addEventListener('focus', refresh)
    window.addEventListener('storage', refresh)
    return () => {
      window.removeEventListener('focus', refresh)
      window.removeEventListener('storage', refresh)
    }
  }, [])

  return (
    <Card>
      <div className="storage-status">
        <div className="storage-status__row">
          <span className="storage-status__label">Concepten op dit apparaat</span>
          <span className="storage-status__value">
            {draftCount === 0 ? 'Geen' : `${draftCount} ${draftCount === 1 ? 'concept' : 'concepten'}`}
          </span>
        </div>
        <div className="storage-status__row">
          <span className="storage-status__label">Cloudverbinding ingesteld</span>
          <span className="storage-status__value">{cloud ? 'Ja' : 'Nee'}</span>
        </div>
        <div className="storage-status__row">
          <span className="storage-status__label">Wacht op synchronisatie</span>
          <span className="storage-status__value">
            {pendingWrites === 0 ? 'Niets' : `${pendingWrites} ${pendingWrites === 1 ? 'schrijfactie' : 'schrijfacties'}`}
          </span>
        </div>
        {!online && (
          <div className="storage-status__row">
            <span className="storage-status__label">Verbinding</span>
            <span className="storage-status__value">Offline — wijzigingen worden bewaard</span>
          </div>
        )}
        {!cloud && (
          <div className="storage-status__row">
            <span className="storage-status__label">
              <Link to={PATHS.app.data}>Meer over je gegevens</Link>
            </span>
            <span className="storage-status__value" />
          </div>
        )}
      </div>
    </Card>
  )
}
